/**
 * Вкладка «Bitrix24» в карточке организации (v0.9.61).
 * Live-выгрузка через 5-минутный кэш backend'а:
 *  - Контакты компании (#9.2)
 *  - Сделки (#9.3 + #9.4)
 *  - Задачи (#9.32)
 *  - Готовые документы из docgen (#9.41)
 *  - Timeline-комментарии менеджеров (#9.6)
 */
import { useEffect, useState } from 'react';
import {
  Briefcase, Calendar, CheckCircle2, ChevronDown, ChevronRight, Database, Download,
  ExternalLink, FileText, Globe, Loader2, Mail, MessageSquare, Phone, RefreshCw,
  Sparkles, User as UserIcon, Users,
} from 'lucide-react';
import { api } from '../api/client';
import { fmtTime } from '../lib/format';

interface BContact { id: number; name: string; post: string; emails: string[]; phones: string[]; created: string }
interface BTask { id: number; title: string; status: string; deadline: string | null; responsible_name: string; created: string | null; closed: string | null; url: string }
interface BDeal { id: number; title: string; semantic: string; date_create: string; close_date: string; amount: string; currency: string; responsible_name: string; url: string }
interface BDoc { id: number | string; title: string; number: string; created: string; pdf_url: string; deal_id: number }
interface BComment { id: string; text: string; created: string; author_name: string; owner_type: string; owner_url: string; owner_id?: number }
interface BUf { key: string; label: string; type: string; value: any }
interface BWhois { domain: string; registrar?: string; created?: string; expires?: string; days_left?: number; nameservers?: string[]; error?: string }

const STATUS_BADGE: Record<string, string> = {
  '1': 'bg-slate-700 text-slate-300',
  '2': 'bg-sky-900/50 text-sky-300',
  '3': 'bg-amber-900/50 text-amber-300',
  '4': 'bg-purple-900/50 text-purple-300',
  '5': 'bg-emerald-900/50 text-emerald-300',
  '6': 'bg-rose-900/50 text-rose-300',
  '7': 'bg-slate-800 text-slate-400',
};
const STATUS_LABEL: Record<string, string> = {
  '1': 'Новая', '2': 'Ждёт', '3': 'В работе', '4': 'На контроле',
  '5': 'Завершена', '6': 'Отложена', '7': 'Отклонена',
};
const SEM_BADGE: Record<string, string> = {
  active: 'bg-sky-900/50 text-sky-300',
  won: 'bg-emerald-900/50 text-emerald-300',
  lost: 'bg-rose-900/50 text-rose-300',
};
const SEM_LABEL: Record<string, string> = {
  active: 'Активная', won: 'Выиграна', lost: 'Проиграна',
};

// Удаляем HTML-теги из B24 timeline-комментариев (там часто HTML), сохраняем
// переносы строк. XSS-safe: только текст, никакого innerHTML.
function stripHtml(s: string | null | undefined): string {
  if (!s) return '';
  return s
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

export default function OrgBitrixTab({ orgId }: { orgId: string }) {
  return (
    <div className="space-y-4">
      <Section icon={Users} title="Контакты компании" loadKey="contacts" orgId={orgId}>
        {(data: { contacts: BContact[]; total: number; reason?: string }) =>
          data.reason ? (
            <Empty reason={data.reason} />
          ) : data.total === 0 ? (
            <Empty reason="В B24 контактов компании не найдено." />
          ) : (
            <ul className="space-y-1.5">
              {data.contacts.map(c => (
                <li key={c.id} className="bg-slate-950/40 border border-slate-800 rounded px-3 py-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-sm text-slate-200 inline-flex items-center gap-1">
                      <UserIcon size={11} className="text-slate-500" /> {c.name}
                    </div>
                    {c.post && <span className="text-xs text-slate-500">{c.post}</span>}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                    {c.emails.map(e => (
                      <a key={e} href={`mailto:${e}`} className="text-blue-300 hover:underline inline-flex items-center gap-1">
                        <Mail size={9} /> {e}
                      </a>
                    ))}
                    {c.phones.map(p => (
                      <a key={p} href={`tel:${p}`} className="text-slate-300 hover:text-blue-300 inline-flex items-center gap-1">
                        <Phone size={9} /> {p}
                      </a>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
      </Section>

      <Section icon={Briefcase} title="Сделки в B24" loadKey="deals" orgId={orgId}>
        {(data: { deals: BDeal[]; total: number; active: number; won: number; lost: number; reason?: string }) =>
          data.reason ? <Empty reason={data.reason} /> :
          data.total === 0 ? <Empty reason="Сделок не найдено." /> : (
            <>
              <div className="flex gap-2 text-xs mb-2">
                <span className="text-slate-500">Всего:</span>
                <span className="text-slate-200">{data.total}</span>
                <span className="text-sky-300">· активных {data.active}</span>
                <span className="text-emerald-300">· выиграно {data.won}</span>
                <span className="text-rose-300">· проиграно {data.lost}</span>
              </div>
              <ul className="space-y-1.5">
                {data.deals.map(d => (
                  <li key={d.id} className="bg-slate-950/40 border border-slate-800 rounded px-3 py-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <a href={d.url} target="_blank" rel="noopener noreferrer"
                         className="text-sm text-blue-300 hover:underline inline-flex items-center gap-1">
                        {d.title} <ExternalLink size={9} />
                      </a>
                      <span className={`px-2 py-0.5 rounded text-[10px] ${SEM_BADGE[d.semantic] || 'bg-slate-700 text-slate-300'}`}>
                        {SEM_LABEL[d.semantic] || d.semantic}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500 mt-1">
                      {d.amount && d.amount !== '0' && (
                        <span className="text-slate-300">{Number(d.amount).toLocaleString('ru')} {d.currency}</span>
                      )}
                      {d.responsible_name && <span>отв. {d.responsible_name}</span>}
                      {d.date_create && <span>создана {d.date_create.slice(0, 10)}</span>}
                      {d.close_date && <span>закрытие {d.close_date.slice(0, 10)}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
      </Section>

      <Section icon={CheckCircle2} title="Задачи клиента" loadKey="tasks" orgId={orgId}>
        {(data: { tasks: BTask[]; total: number; open: number; reason?: string }) =>
          data.reason ? <Empty reason={data.reason} /> :
          data.total === 0 ? <Empty reason="Задач по компании в B24 нет." /> : (
            <>
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <span className="text-xs text-slate-500">Всего {data.total}, открытых {data.open}</span>
                <button onClick={async (e) => {
                  e.stopPropagation();
                  if (!confirm(`Скачать все файлы из ${data.total} задач(и) клиента и сохранить в SD attachments? Дубликаты пропустятся.`)) return;
                  try {
                    const { data: r } = await api.post(`/api/v1/organizations/${orgId}/bitrix-task-files/import`);
                    alert(`Импорт завершён:\n• Задач просмотрено: ${r.tasks_scanned}\n• Файлов сохранено: ${r.files_imported}\n• Пропущено (дубли): ${r.skipped}\n${r.errors?.length ? '• Ошибок: ' + r.errors.length : ''}`);
                  } catch (e: any) {
                    alert('Импорт не удался: ' + (e?.response?.data?.detail || e?.message || 'ошибка'));
                  }
                }}
                  className="text-[11px] bg-slate-800 hover:bg-emerald-900/50 text-slate-300 hover:text-emerald-300 px-2 py-1 rounded inline-flex items-center gap-1">
                  <Download size={11} /> Импортировать файлы в SD
                </button>
              </div>
              <ul className="space-y-1.5">
                {data.tasks.map(t => (
                  <li key={t.id} className={`bg-slate-950/40 border border-slate-800 rounded px-3 py-2 ${t.closed ? 'opacity-60' : ''}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <a href={t.url} target="_blank" rel="noopener noreferrer"
                         className="text-sm text-blue-300 hover:underline inline-flex items-center gap-1">
                        {t.title} <ExternalLink size={9} />
                      </a>
                      <span className={`px-2 py-0.5 rounded text-[10px] ${STATUS_BADGE[t.status] || 'bg-slate-700 text-slate-300'}`}>
                        {STATUS_LABEL[t.status] || t.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500 mt-1">
                      {t.deadline && (
                        <span className="inline-flex items-center gap-1 text-slate-300">
                          <Calendar size={9} /> {t.deadline.slice(0, 10)}
                        </span>
                      )}
                      {t.responsible_name && <span>отв. {t.responsible_name}</span>}
                      {t.closed && <span>закрыта {t.closed.slice(0, 10)}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
      </Section>

      <Section icon={FileText} title="Документы из docgen" loadKey="docgen" orgId={orgId}>
        {(data: { documents: BDoc[]; total: number }) =>
          data.total === 0 ? <Empty reason="Готовых документов B24 не нашлось." /> : (
            <ul className="space-y-1.5">
              {data.documents.map(d => (
                <li key={d.id} className="bg-slate-950/40 border border-slate-800 rounded px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm text-slate-200 inline-flex items-center gap-1">
                    <FileText size={11} className="text-slate-500" /> {d.title}
                    {d.number && <span className="text-slate-500 ml-1 text-xs">№ {d.number}</span>}
                  </div>
                  <div className="flex gap-2 text-xs">
                    {d.created && <span className="text-slate-500">{d.created.slice(0, 10)}</span>}
                    {d.pdf_url && (
                      <a href={d.pdf_url} target="_blank" rel="noopener noreferrer"
                         className="text-blue-300 hover:underline inline-flex items-center gap-1">
                        PDF <ExternalLink size={9} />
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
      </Section>

      <Section icon={Database} title="Кастомные поля компании (UF)" loadKey="uf" orgId={orgId}>
        {(data: { fields: BUf[]; total: number; reason?: string }) =>
          data.reason ? <Empty reason={data.reason} /> :
          data.total === 0 ? <Empty reason="UF полей у компании нет (или не заполнены)." /> : (
            <dl className="divide-y divide-slate-800">
              {data.fields.map(f => (
                <div key={f.key} className="grid grid-cols-[200px_1fr] gap-3 px-2 py-1.5 text-sm">
                  <dt className="text-slate-500 text-xs truncate" title={f.key}>{f.label || f.key}</dt>
                  <dd className="text-slate-200 text-xs whitespace-pre-wrap">
                    {Array.isArray(f.value) ? f.value.join(', ') : String(f.value)}
                  </dd>
                </div>
              ))}
            </dl>
          )}
      </Section>

      <Section icon={Globe} title="Whois доменов" loadKey="whois" orgId={orgId}>
        {(data: { items: BWhois[]; total: number; reason?: string }) =>
          data.reason ? <Empty reason={data.reason} /> :
          data.total === 0 ? <Empty reason="Доменов не указано." /> : (
            <ul className="space-y-1.5">
              {data.items.map(it => (
                <li key={it.domain} className="bg-slate-950/40 border border-slate-800 rounded px-3 py-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <a href={`https://${it.domain}`} target="_blank" rel="noopener noreferrer"
                       className="text-sm text-blue-300 hover:underline inline-flex items-center gap-1">
                      {it.domain} <ExternalLink size={9} />
                    </a>
                    {it.days_left !== undefined && it.days_left !== null && (
                      <span className={`text-[10px] px-2 py-0.5 rounded ${
                        it.days_left < 30 ? 'bg-rose-900/50 text-rose-300' :
                        it.days_left < 90 ? 'bg-amber-900/50 text-amber-300' :
                        'bg-emerald-900/50 text-emerald-300'
                      }`}>
                        {it.days_left < 0 ? `истёк ${-it.days_left}д назад` : `${it.days_left}д до истечения`}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-x-3">
                    {it.registrar && <span>Регистратор: <span className="text-slate-300">{it.registrar}</span></span>}
                    {it.created && <span>Создан: {it.created.slice(0, 10)}</span>}
                    {it.expires && <span>Истекает: {it.expires.slice(0, 10)}</span>}
                    {it.error && <span className="text-amber-300">⚠ {it.error.slice(0, 80)}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
      </Section>

      <Section icon={MessageSquare} title="Комментарии менеджеров" loadKey="timeline" orgId={orgId}>
        {(data: { comments: BComment[]; total: number; reason?: string }) =>
          data.reason ? <Empty reason={data.reason} /> :
          data.total === 0 ? <Empty reason="Комментариев в B24 timeline не найдено." /> : (
            <ul className="space-y-1.5">
              {data.comments.slice(0, 30).map(c => (
                <li key={c.id} className="bg-slate-950/40 border border-slate-800 rounded px-3 py-2">
                  <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                    <span className="text-slate-300">{c.author_name || '—'}</span>
                    <span>·</span>
                    <span>{fmtTime(c.created)}</span>
                    <span>·</span>
                    <a href={c.owner_url} target="_blank" rel="noopener noreferrer"
                       className="text-blue-300 hover:underline">
                      {c.owner_type === 'company' ? 'компания' : `сделка #${c.owner_id || ''}`}
                    </a>
                  </div>
                  {/* XSS-safe: B24 HTML удаляем, сохраняем только текст с переносами */}
                  <div className="mt-1 text-sm text-slate-200 whitespace-pre-wrap">
                    {stripHtml(c.text)}
                  </div>
                </li>
              ))}
            </ul>
          )}
      </Section>
    </div>
  );
}

function Empty({ reason }: { reason: string }) {
  return <div className="text-xs text-slate-500 text-center py-3">{reason}</div>;
}

function Section<T extends Record<string, any>>({ orgId, loadKey, title, icon: Icon, children }: {
  orgId: string; loadKey: string; title: string;
  icon: any;
  children: (data: T) => React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get(`/api/v1/organizations/${orgId}/bitrix-${loadKey}`);
      setData(data);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open && data === null && !loading) load(); }, [open]);

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-4 py-3 border-b border-slate-800 flex items-center justify-between cursor-pointer hover:bg-slate-800/40"
              onClick={() => setOpen(o => !o)}>
        <h3 className="font-semibold text-white text-sm inline-flex items-center gap-2">
          {open ? <ChevronDown size={12} className="text-slate-500" /> : <ChevronRight size={12} className="text-slate-500" />}
          <Icon size={14} className="text-blue-400" /> {title}
        </h3>
        <button onClick={(e) => { e.stopPropagation(); load(); }}
                className="text-slate-500 hover:text-slate-200 p-0.5"
                title="Обновить">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
        </button>
      </header>
      {open && (
        <div className="p-3">
          {loading && data === null ? (
            <div className="flex justify-center py-3"><Loader2 size={14} className="animate-spin text-blue-400" /></div>
          ) : err ? (
            <div className="text-xs text-rose-300">{err}</div>
          ) : data ? children(data) : null}
        </div>
      )}
    </section>
  );
}
